/** @jsxImportSource @emotion/react */
import { Grid as MuiGrid, Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { Alert, Spinner } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { Accordion, Divider, Tooltip, ZipPlusMMPDialog } from 'components';
import { useMerchantData } from 'contexts';
import { FeatureFlag, ProductClassification } from 'enums';
import { Constants, css } from 'global';
import { useFeatureFlagEnabled } from 'hooks';
import _ from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { RepaymentCalculations } from 'types';
import { currencyFormatter, productFormatter } from 'utils';
import * as styles from './RepaymentsCalculatorModule.styles';
import { RepaymentsCalculatorModuleProps } from './RepaymentsCalculatorModuleProps';

enum RepaymentFrequency {
  Weekly = 'weekly',
  Fortnightly = 'fortnightly',
  Monthly = 'monthly',
}

const RepaymentsCalculatorModule: FC<RepaymentsCalculatorModuleProps> = ({
  repaymentsQuote,
  selectedProductClassification,
  isLoading,
  isNewCustomer,
  formHasError,
  price,
}) => {
  // Variables
  const [selectedQuote, setSelectedQuote] = useState<RepaymentCalculations>();
  const [isQuoteEmpty, setIsQuoteEmpty] = useState<boolean>(true);
  const [selectedRepaymentFrequency, setSelectedRepaymentFrequency] =
    useState<RepaymentFrequency>(RepaymentFrequency.Monthly);
  const [isZipPlusMmpDialogOpen, setIsZipPlusMmpDialogOpen] =
    useState<boolean>(false);

  const { merchantProductsForDisplay } = useMerchantData();

  const ZipPlusInterestFreeThreshold = currencyFormatter(
    Constants.productDefaults['Zip Plus'].interestFreeThreshold,
    false
  );

  // Experiments
  const zipPlusAcquiPhase2Enabled = useFeatureFlagEnabled(
    FeatureFlag.ZipPlusAcquiPhaseTwo
  );

  const defaultProductSetup = useMemo(() => {
    return Constants.productDefaults[
      productFormatter(selectedProductClassification)
    ];
  }, [selectedProductClassification]);

  useEffect(() => {
    if (selectedProductClassification === ProductClassification.ZipMoney) {
      setSelectedRepaymentFrequency(RepaymentFrequency.Monthly);
    }
  }, [selectedProductClassification]);

  // Functions
  function merchantTransactionLimit(
    classification: ProductClassification
  ): string {
    return currencyFormatter(
      _.max(
        merchantProductsForDisplay
          ?.filter(
            (merchantProduct) =>
              merchantProduct.productClassification == classification
          )
          .map((merchantProduct) => merchantProduct.transactionLimitMax)
      ) ??
        Constants.productDefaults[productFormatter(classification)]
          .TransactionLimit,
      false
    );
  }

  function getQuoteValue<K extends keyof RepaymentCalculations>(
    quoteKey: K
  ): number {
    const defaultValue = defaultProductSetup[quoteKey] as number;
    return !formHasError
      ? (selectedQuote?.[quoteKey] ?? defaultValue)
      : defaultValue;
  }

  function getMonthlyAccountFee(): number {
    return getQuoteValue('monthlyAccountFee');
  }

  function getMinimumMonthlyPayment(): number {
    return getQuoteValue('minimumMonthlyRepayment');
  }

  function getMinimumMonthlyRepaymentPercentage(): number {
    return getQuoteValue('minimumMonthlyRepaymentPercentage');
  }

  function getZMMinimumMonthlyRepayment(): number {
    if (formHasError || !price) {
      return getMinimumMonthlyPayment();
    }

    const minPercentagePayment =
      (getMinimumMonthlyRepaymentPercentage() / 100) * price;

    return Math.max(minPercentagePayment, getMinimumMonthlyPayment());
  }

  function getFirstMonthRepayment(): number {
    const amountToPay =
      ((price + getMonthlyAccountFee() + getEstablishmentFee()) *
        getMinimumMonthlyRepaymentPercentage()) /
      100;

    return Math.max(amountToPay, getMinimumMonthlyPayment());
  }

  function getEstablishmentFee(): number {
    if (!isNewCustomer || formHasError) {
      return 0;
    }
    return selectedQuote?.establishmentFee ?? 0;
  }

  function convertAndFormatPayment(monthlyAmount: number): string {
    switch (selectedRepaymentFrequency) {
      case RepaymentFrequency.Fortnightly:
        return currencyFormatter(
          monthlyAmount / Constants.timeframe.fortnightsInAMonth
        );
      case RepaymentFrequency.Weekly:
        return currencyFormatter(
          monthlyAmount / Constants.timeframe.weeksInAMonth
        );
      case RepaymentFrequency.Monthly:
      default:
        return currencyFormatter(monthlyAmount);
    }
  }

  function convertPaymentPercentage(monthlyPercentage: number): string {
    let convertedPercentage: number;
    switch (selectedRepaymentFrequency) {
      case RepaymentFrequency.Fortnightly:
        convertedPercentage =
          monthlyPercentage / Constants.timeframe.fortnightsInAMonth;
        break;
      case RepaymentFrequency.Weekly:
        convertedPercentage =
          monthlyPercentage / Constants.timeframe.weeksInAMonth;
        break;
      case RepaymentFrequency.Monthly:
      default:
        convertedPercentage = monthlyPercentage;
        break;
    }
    return `${Number.isInteger(convertedPercentage) ? convertedPercentage : convertedPercentage.toFixed(2)}%`;
  }

  // Elements
  function productDescription(): JSX.Element {
    let productDescription: string;
    const transactionLimit = merchantTransactionLimit(
      selectedProductClassification
    );
    switch (selectedProductClassification) {
      case ProductClassification.ZipPay:
        productDescription = `Use Zip Pay for any purchases up to${' '}
          ${transactionLimit}, interest free always.`;
        break;
      case ProductClassification.ZipPlus:
        productDescription = `For any purchases up to ${transactionLimit}. Interest free when the amount owing is ${ZipPlusInterestFreeThreshold} or less at the end of the month.`;
        break;
    }

    return (
      <>
        {zipPlusAcquiPhase2Enabled &&
        selectedProductClassification == ProductClassification.ZipPay ? (
          <Alert variant="info" css={styles.upgradeAlert}>
            This customer may be eligible for an upgrade to Zip Plus.{' '}
            <a href="https://zip.co/au/zip-plus" target="_blank">
              Learn more
            </a>
          </Alert>
        ) : (
          <p className="body2" css={css.marginBottom(0)}>
            {productDescription}
          </p>
        )}
      </>
    );
  }
  const productDisclaimer = (): JSX.Element => {
    const { abn, creditLicence } = Constants.zipInfo;
    switch (selectedProductClassification) {
      case ProductClassification.ZipPlus: {
        const {
          interestFreeThreshold,
          interestRate: { percent },
          monthlyAccountFee,
        } = Constants.productDefaults['Zip Plus'];
        return (
          <div>
            <p>
              Interest accrues daily on the total balance owing at the end of
              each day. No interest will be charged to your account when your
              balance is under {currencyFormatter(interestFreeThreshold, false)}{' '}
              (including where it is paid in full) at the end of the last day of
              each month. Each day ends at 11:59pm.
            </p>
            <p>
              Pay your closing balance in full by the end of each calendar month
              and we'll also waive the monthly account fee of{' '}
              {currencyFormatter(monthlyAccountFee)} for that month. A monthly
              account fee of {currencyFormatter(monthlyAccountFee)} will
              otherwise apply. Standard interest rate is {percent}% p.a.
            </p>
            <br />
            <p>
              T&Cs and credit approval criteria apply. Interest and other
              charges may be payable, see T&Cs. Interest, fees and charges
              subject to change.
            </p>
            <p>
              Credit provided by ZipMoney Payments Pty Ltd (ABN {abn}),
              Australian Credit Licence Number {creditLicence}.
            </p>
          </div>
        );
      }
      case ProductClassification.ZipMoney: {
        const { monthlyAccountFee, interestRate } =
          Constants.productDefaults['Zip Money'];
        return (
          <div>
            <p>
              The interest free period is the maximum period of time when no
              interest is charged on the purchase. The availability of the
              interest free period for a purchase will depend on the purchase
              amount, the minimum monthly repayment and account status. Minimum
              monthly payments are required and vary depending on your closing
              balance at the end of each statement period. Minimum monthly
              repayments may require repayment of the purchase prior to the
              expiry of the maximum interest free period. In other cases, paying
              only the minimum monthly repayment may not repay the purchase in
              the interest free period. Any purchase amount outstanding at the
              expiry of the interest free period will be charged at the standard
              variable interest rate, {interestRate.percent}% per annum, as at 1
              June 2023. Zip Money is available to approved applicants and
              subject to completion of a satisfactory credit assessment. A
              monthly account fee of ${monthlyAccountFee} applies and a one-off
              establishment fee may apply for new customers. Other charges may
              be payable, see T&Cs. Interest, fees and charges are subject to
              change. Terms and Conditions apply and are available on
              application.{' '}
              <a href="https://zip.co/au" target="_blank">
                Further details
              </a>
              . Credit provided by ZipMoney Payments Pty Ltd (ABN {abn}),
              Australian Credit Licence Number {creditLicence}.
            </p>
          </div>
        );
      }
      default:
        return (
          <div>
            <p>
              Available to approved applicants only and subject to completion of
              satisfactory credit check.
            </p>
            <p>
              Minimum monthly repayments are required. Paying only the minimum
              monthly repayment amount will generally not repay the purchase
              within the interest free period.
            </p>
            <p>
              A monthly account fee will apply and a one-off establishment fee
              may apply for new customers.
            </p>
            <p>
              Other charges may be payable, see T&Cs. Interest, fees and charges
              are subject to change.
            </p>
            <br />
            <p>
              Terms & conditions apply and are available on application. See
              your contract for further details.
            </p>
            <p>
              Credit provided by ZipMoney Payments Pty Ltd (ABN {abn}),
              Australian Credit Licence Number {creditLicence}.
            </p>
          </div>
        );
    }
  };

  function orderSummaryTile(): JSX.Element {
    return (
      <MuiGrid
        container
        size={{ xs: 12 }}
        css={[styles.frequencyTile, css.marginBottom(10)]}
      >
        <MuiGrid
          size={{ xs: 12 }}
          css={[styles.whiteCard('lg'), styles.balanceWhiteCard]}
        >
          {selectedProductClassification == ProductClassification.ZipMoney &&
            isNewCustomer && (
              <>
                {/* Purchase amount section */}
                <div
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <p className="body2" css={styles.calculatorLabel}>
                    Purchase amount
                  </p>
                  <p className="body1" css={[styles.calculatorLabelBold]}>
                    {price ? currencyFormatter(price) : '-'}
                  </p>
                </div>
                <Divider top={16} bottom={16} />

                {/* Establishment fee section */}
                <div
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <p className="body2" css={styles.calculatorLabel}>
                    Establishment fee
                  </p>
                  <p className="body1" css={[styles.calculatorLabelBold]}>
                    {currencyFormatter(getEstablishmentFee())}
                  </p>
                </div>
                <Divider top={16} bottom={16} />
              </>
            )}

          {/* Total purchase amount section */}
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <p className="heading3" css={styles.purchaseAmount}>
              Total purchase amount
            </p>
            <p className="heading3" css={styles.purchaseAmount}>
              {price ? currencyFormatter(price + getEstablishmentFee()) : '-'}
            </p>
          </div>
          <div className="body4" css={styles.calculatorFineprint}>
            This figure does not include monthly fees or interest charges that
            may be payable.
          </div>
        </MuiGrid>
      </MuiGrid>
    );
  }

  function frequencyTile(): JSX.Element {
    // Zip Money repayment tile - New 2025 version
    if (selectedProductClassification == ProductClassification.ZipMoney) {
      return (
        <MuiGrid
          container
          size={{ xs: 12 }}
          css={[styles.frequencyTile, css.marginBottom(10)]}
        >
          <MuiGrid
            size={{ xs: 12 }}
            css={[styles.whiteCard('lg'), styles.balanceWhiteCard]}
          >
            {/* Monthly Repayment amount section */}
            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div className="body2" css={styles.calculatorLabel}>
                <div>Minimum {selectedRepaymentFrequency} repayment</div>
                <div>(whichever is of greater value)</div>
              </div>
              <div className="body1" css={[styles.calculatorLabelBold]}>
                {convertPaymentPercentage(
                  getMinimumMonthlyRepaymentPercentage()
                )}{' '}
                or {convertAndFormatPayment(getMinimumMonthlyPayment())}
              </div>
            </div>
            <div className="body4" css={styles.calculatorFineprint}>
              Minimum monthly repayments are calculated as the higher of:
              <p>
                <span css={styles.bulletPoint} />
                {convertPaymentPercentage(
                  getMinimumMonthlyRepaymentPercentage()
                )}{' '}
                of the customer{'\u2019'}s total closing balance
              </p>
              <p>
                <span css={styles.bulletPoint} />
                {convertAndFormatPayment(getMinimumMonthlyPayment())}.
              </p>
              <br />
              {isNewCustomer ? (
                <>
                  Transactions made after this purchase may affect the minimum
                  monthly repayment.
                </>
              ) : (
                <>
                  Existing balances or transactions made after this purchase may
                  affect the minimum monthly repayment.
                </>
              )}
            </div>

            <Divider top={16} bottom={16} />

            {/* Monthly account fee section */}
            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div css={styles.tooltipWithIconContainer}>
                <p className="body2" css={[styles.calculatorLabel]}>
                  Monthly account fee
                </p>
                <Tooltip
                  content={
                    <div css={styles.tooltipContent}>
                      <p className="body1">Monthly account fee</p>
                      <p className="body4">
                        The monthly fee is waived if the customer's balance is
                        $0 at the end of the month.
                      </p>
                    </div>
                  }
                  placement="bottom"
                  enterDelay={20}
                  leaveDelay={1000}
                  maxWidth={240}
                  arrow
                  variant="primary"
                >
                  <Icons.InformationCircle
                    width="16"
                    height="16"
                    css={styles.tooltipIcon}
                  />
                </Tooltip>
              </div>
              <p className="body1" css={[styles.calculatorLabelBold]}>
                {currencyFormatter(getMonthlyAccountFee())}
              </p>
            </div>
          </MuiGrid>
          {!isNewCustomer ? (
            <MuiGrid
              size={{ xs: 12 }}
              css={[
                styles.whiteCard('lg'),
                styles.balanceWhiteCard,
                css.marginTop(16),
              ]}
            >
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <p className="body1" css={styles.calculatorLabel}>
                  What are the repayments for this order?
                </p>
              </div>
              <div className="body4" css={styles.calculatorFineprint}>
                Existing customers can manage their repayments via the app.
              </div>
            </MuiGrid>
          ) : (
            <MuiGrid
              size={{ xs: 12 }}
              css={[
                styles.whiteCard('lg'),
                styles.balanceWhiteCard,
                css.marginTop(16),
              ]}
            >
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <p className="body1" css={styles.calculatorLabel}>
                  First minimum monthly repayment for this order
                </p>
                <p className="body1" css={[styles.calculatorLabelBold]}>
                  {price
                    ? convertAndFormatPayment(getFirstMonthRepayment())
                    : '-'}
                </p>
              </div>
              <div className="body4" css={styles.calculatorFineprint}>
                Amount inclusive of total order amount, establishment fee and
                monthly account fee. No interest is applicable to the first
                repayment calculation.
                <br />
                <br />
                This amount is indicative only and is calculated based on:
                <br />
                {getFirstMonthRepayment() > getZMMinimumMonthlyRepayment() ? (
                  <>
                    <span css={styles.bulletPoint} />
                    {convertPaymentPercentage(
                      getMinimumMonthlyRepaymentPercentage()
                    )}{' '}
                    of the customer's closing balance being greater than the
                    fixed minimum monthly repayment of{' '}
                    {convertAndFormatPayment(getMinimumMonthlyPayment())}; and
                  </>
                ) : (
                  <>
                    <span css={styles.bulletPoint} />
                    The fixed minimum monthly repayment of{' '}
                    {convertAndFormatPayment(getMinimumMonthlyPayment())} being
                    greater than the{' '}
                    {convertPaymentPercentage(
                      getMinimumMonthlyRepaymentPercentage()
                    )}{' '}
                    of the customer's closing balance; and
                  </>
                )}
                <br />
                <span css={styles.bulletPoint} />
                the customer having no existing balance on their account.
                <br />
                <br />
                If the customer has an existing balance on their account, or
                their closing balance changes (including after making this
                purchase), the first repayment may be different from the amount
                shown above.
                <br />
                <br />
                An interest free period applies to this purchase. Subsequent
                minimum monthly repayments may be different to the amount shown
                above, including due to interest charges that may be payable
                when the interest free period applicable to this purchase
                expires.
              </div>
            </MuiGrid>
          )}
        </MuiGrid>
      );
    }

    // ZipPay, ZipPlus repayment tile
    return (
      <MuiGrid
        container
        sx={{ flexGrow: 1 }}
        size={{ xs: 12 }}
        css={[styles.whiteCard('md'), styles.frequencyTile]}
      >
        <MuiGrid size={{ xs: 12 }}>
          <MuiTabs
            value={selectedRepaymentFrequency}
            onChange={(e, value): void => setSelectedRepaymentFrequency(value)}
            indicatorColor="primary"
            textColor="primary"
            centered
            css={styles.tabs}
            selectionFollowsFocus
          >
            <MuiTab
              id="0"
              value={RepaymentFrequency.Weekly}
              label="Weekly"
              css={styles.frequencyTab}
            />
            <MuiTab
              id="1"
              value={RepaymentFrequency.Fortnightly}
              label="Fortnightly"
              css={styles.frequencyTab}
            />
            <MuiTab
              id="2"
              value={RepaymentFrequency.Monthly}
              label="Monthly"
              css={styles.frequencyTab}
            />
          </MuiTabs>
        </MuiGrid>
        <MuiGrid
          css={[
            styles.whiteCard('lg'),
            styles.balanceWhiteCard,
            styles.minimumMonthlyPayment,
          ]}
        >
          <p className="body1">Min. {selectedRepaymentFrequency} payment</p>
          <p className="heading1">
            {convertAndFormatPayment(getMinimumMonthlyPayment())}
          </p>
        </MuiGrid>
        <MuiGrid
          css={[
            styles.whiteCard('lg'),
            styles.balanceWhiteCard,
            styles.monthlyAccountFee,
          ]}
        >
          <p className="body1" css={styles.tooltipWithIconContainer}>
            Monthly account fee
            <Tooltip
              content={
                <div css={styles.tooltipContent}>
                  <p className="body1">Monthly account fee</p>
                  <p className="body4">
                    No fee when you have $0 balance at the end of the month.
                  </p>
                </div>
              }
              placement="bottom"
              enterDelay={20}
              leaveDelay={1000}
              maxWidth={240}
              arrow
              variant="primary"
            >
              <Icons.InformationCircle
                width="16"
                height="16"
                css={styles.tooltipIcon}
              />
            </Tooltip>
          </p>

          <p className="heading1" css={isLoading && styles.mask}>
            {currencyFormatter(getMonthlyAccountFee())}
          </p>
        </MuiGrid>
        {selectedProductClassification == ProductClassification.ZipPay && (
          <MuiGrid
            size={{ xs: 12 }}
            css={[styles.whiteCard('lg'), styles.balanceWhiteCard]}
          >
            <p className="body1" css={css.marginBottom(12)}>
              Interest
            </p>
            <p className="heading1">Interest free always</p>
          </MuiGrid>
        )}
        {selectedProductClassification == ProductClassification.ZipPlus && (
          <MuiGrid
            size={{ xs: 12 }}
            css={[styles.whiteCard('lg'), styles.balanceWhiteCard]}
          >
            <p
              className="body1"
              css={[css.marginBottom(12), styles.tooltipWithIconContainer]}
            >
              Interest
              <Tooltip
                content={
                  <div css={styles.tooltipContent}>
                    <p className="body1">Interest</p>
                    <p className="body4">
                      Interest applies if the total balance exceeds{' '}
                      {currencyFormatter(
                        Constants.productDefaults['Zip Plus']
                          .interestFreeThreshold,
                        false
                      )}{' '}
                      at end of the month.
                    </p>
                  </div>
                }
                placement="bottom"
                enterDelay={20}
                leaveDelay={1000}
                maxWidth={240}
                arrow
                dense
                variant="primary"
              >
                <Icons.InformationCircle
                  width="16"
                  height="16"
                  css={styles.tooltipIcon}
                />
              </Tooltip>
            </p>

            <p className="heading1">
              {Constants.productDefaults['Zip Plus'].interestRate.percent}%
              <span className="body4"> p.a</span>
            </p>
          </MuiGrid>
        )}
      </MuiGrid>
    );
  }

  // UseEffects
  useEffect(() => {
    // retrieve the productClassification specific quote from the RepaymentQuoteResponse.
    // so that the module can populate with the correct details.
    // If quote does not exist then set to null in order to show the empty state.
    if (repaymentsQuote) {
      const quoteForProduct =
        repaymentsQuote[productFormatter(selectedProductClassification)];

      setIsQuoteEmpty(!quoteForProduct);
      setSelectedQuote(quoteForProduct);
    }
  }, [repaymentsQuote, selectedProductClassification]);

  // HTML module
  return !repaymentsQuote || isQuoteEmpty ? (
    <div css={[styles.base, styles.emptyState]}>
      <img
        src={Constants.assets.illustrations.calculator}
        alt="Calculator"
        width={96}
      />
      <p className="title3">Enter order information to get started</p>
      <p className="body2">
        The calculator will appear here when order details have been entered.
      </p>
      {isLoading && <Spinner />}
    </div>
  ) : (
    <div css={styles.moduleContainer}>
      <MuiGrid container sx={{ flexGrow: 1 }} css={styles.base}>
        <img
          src={
            Constants.assets.productLogos[
              productFormatter(selectedProductClassification)
            ].light
          }
          alt={productFormatter(selectedProductClassification)}
          css={styles.productHeader}
        />
        <MuiGrid size={{ xs: 12 }} css={styles.productDescription}>
          {productDescription()}
        </MuiGrid>
        {selectedProductClassification != ProductClassification.ZipMoney && (
          <Divider css={[styles.divider]} />
        )}
        {selectedProductClassification == ProductClassification.ZipMoney && (
          <>
            <div css={{ height: '24px' }} />
            <MuiGrid container sx={{ flexGrow: 1 }}>
              <p className="heading2">Order summary</p>
              {orderSummaryTile()}
              <p className="heading2" css={css.marginTop(24)}>
                Repayments
              </p>
              <p className="body2" css={css.marginTop(0)}>
                <a
                  href={Constants.helpArticles.zipMoneyRepayment}
                  target="_blank"
                >
                  Flexible repayments
                </a>{' '}
                give your customers the choice to pay weekly, fortnightly, or
                monthly.
              </p>
              {frequencyTile()}
            </MuiGrid>
          </>
        )}
        {selectedProductClassification == ProductClassification.ZipPay && (
          <MuiGrid container sx={{ flexGrow: 1 }}>
            <p className="body2" css={css.marginTop(0)}>
              This customer account is set to{' '}
              <a href={Constants.helpArticles.zipPayRepayment} target="blank">
                flexible repayments.
              </a>
            </p>
            {frequencyTile()}
            <p className="body2">
              Customer{'\u2019'}s monthly account fee is waived if their
              statement balance is paid by the due date.
            </p>
          </MuiGrid>
        )}
        {selectedProductClassification == ProductClassification.ZipPlus && (
          <MuiGrid container sx={{ flexGrow: 1 }} css={css.marginBottom(16)}>
            <p className="body2" css={css.marginTop(0)}>
              The{' '}
              <a
                onClick={(): void => setIsZipPlusMmpDialogOpen(true)}
                css={styles.zPlusDialogLink}
              >
                minimum monthly repayment
              </a>{' '}
              amount is based on the account credit limit.
            </p>
            {frequencyTile()}
          </MuiGrid>
        )}
        <MuiGrid size={{ xs: 12 }} css={styles.disclaimer}>
          <Accordion
            summary={
              selectedProductClassification ===
              ProductClassification.ZipMoney ? (
                <span>Important information - Please read</span>
              ) : (
                <span>Disclaimer</span>
              )
            }
            details={productDisclaimer()}
            expandIconSize={20}
          />
        </MuiGrid>
      </MuiGrid>

      <ZipPlusMMPDialog
        open={isZipPlusMmpDialogOpen}
        onClose={(): void => setIsZipPlusMmpDialogOpen(false)}
      />
    </div>
  );
};

export default RepaymentsCalculatorModule;
